// b13 Rumaysa — celestial-library "storybook" birthday preview card.
//
// The full template is a midnight-blue + antique-gold storybook (floating
// books, a moon through a gothic arch, drifting balloons & butterflies).
// The catalog row renders the painted hero webp via `cardImage`; this
// procedural card is the registered fallback the catalog contract checks
// for — a midnight panel with a star field, a gold gothic arch, a moon,
// a centred monogram, and the name beneath.
import { type LayoutCtx } from "./_shared";

const STARS: Array<[number, number, number]> = [
  [14, 12, 1.4], [82, 9, 1.2], [70, 22, 1], [22, 30, 1],
  [90, 40, 1.3], [10, 52, 1], [86, 64, 1.1], [30, 74, 1.2],
  [60, 84, 1], [48, 18, 0.9], [38, 46, 0.8], [64, 56, 0.9],
];

export function RumaysaPreviewLayout({
  names,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const gold = accent;
  const goldBright = "#F0DCA0";
  const first = (names || "Rumaysa").trim().charAt(0).toUpperCase();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(120% 90% at 50% 6%, #2A3160 0%, ${bg} 52%, #0A0E1F 100%)`,
        color: ink,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "10% 8%",
        overflow: "hidden",
      }}
    >
      {STARS.map(([x, y, r], i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${x}%`,
            top: `${y}%`,
            width: r * 2,
            height: r * 2,
            borderRadius: "50%",
            background: goldBright,
            opacity: 0.7,
            boxShadow: `0 0 ${r * 3}px ${goldBright}`,
          }}
        />
      ))}

      {/* gothic arch + moon + monogram */}
      <div style={{ position: "relative", width: "56%", aspectRatio: "0.62 / 1", marginBottom: "7%" }}>
        <svg viewBox="0 0 120 200" width="100%" height="100%" aria-hidden style={{ position: "absolute", inset: 0 }}>
          <defs>
            <linearGradient id="rumArch" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={goldBright} stopOpacity="0.9" />
              <stop offset="100%" stopColor={gold} stopOpacity="0.25" />
            </linearGradient>
          </defs>
          {/* outer arch */}
          <path
            d="M14 200 L14 70 Q14 14 60 14 Q106 14 106 70 L106 200"
            fill="none"
            stroke="url(#rumArch)"
            strokeWidth="1.6"
          />
          {/* inner arch */}
          <path
            d="M24 200 L24 74 Q24 26 60 26 Q96 26 96 74 L96 200"
            fill="none"
            stroke={gold}
            strokeWidth="0.6"
            opacity="0.5"
          />
          {/* moon (gold disc with a soft crescent shadow) */}
          <circle cx="60" cy="56" r="13" fill={goldBright} opacity="0.92" />
          <circle cx="65" cy="52" r="13" fill={bg} opacity="0.55" />
          {/* keystone dot */}
          <circle cx="60" cy="16" r="2.4" fill={goldBright} />
        </svg>
        {/* monogram, sitting in the arch interior */}
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", paddingTop: "30%" }}>
          <span style={{ fontFamily: font, fontSize: 34, color: goldBright, lineHeight: 1 }}>{first}</span>
        </div>
      </div>

      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 6.5,
          fontWeight: 700,
          letterSpacing: "0.42em",
          color: gold,
          marginBottom: "4%",
        }}
      >
        TUG'ILGAN KUN
      </div>
      <div style={{ fontFamily: font, fontSize: 30, lineHeight: 1.05, color: ink }}>{names}</div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 6,
          fontWeight: 600,
          letterSpacing: "0.3em",
          color: `${ink}88`,
          marginTop: "5%",
        }}
      >
        FOUR WISHES
      </div>
    </div>
  );
}
